import React, { Fragment, useEffect, useState } from 'react'
import '../../assets/scss/feature-types/feature-types.scss'
import setHeader from '../../components/Functions/setHeader';
import axiosConfig from "../../services/axiosConfig";
import { Link } from 'react-feather';
import { SITE_URL } from '../../services/Const';
import { Breadcrumb } from 'react-bootstrap';
import SweetAlert from "sweetalert2";
import { useNavigate } from 'react-router-dom';
import NoDataHandler from '../../components/common/NoDataHandler';
import LoadingComponent from '../../components/common/LoadingComponent';
import { formatDate } from '../../utils/dateFormatter';

const AllFeatureTypes = () => {
  const [typeData, setTypeData] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getTypeData();
  }, []);

  const DeeleteOneFeaturData = (id) => {
    axiosConfig.get(`delete-feature-type/${id}`, setHeader()).then((res) => {
      if (!res.data.error) {
        // Show success message
        SweetAlert.fire({
          title: "Success",
          text: res.data.messages,
          icon: "success",
        });
        // Refresh the data
        getTypeData();
      } else {
        // Show error message
        SweetAlert.fire({
          title: "Oops!",
          text: res.data.messages,
          icon: "error",
        });
      }
    }).catch((error) => {
      console.error("Error deleting feature type:", error);
      SweetAlert.fire({
        title: "Error",
        text: "An error occurred while deleting the feature type.",
        icon: "error",
      });
    });
  }

  const DeleteAllSectionData = (sectionName) => {
    const sectionProducts = typeData.filter(item => item.section_name === sectionName);

    if (sectionProducts.length === 0) {
      SweetAlert.fire({
        title: "No Data",
        text: "No products found in this section to delete.",
        icon: "info",
      });
      return;
    }

    SweetAlert.fire({
      title: "Are you sure?",
      text: `This will delete all ${sectionProducts.length} products in the "${sectionName}" section. You will not be able to recover this data!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete All",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        // Get all product IDs from the current section
        const productIds = sectionProducts.map(item => item.f_id);

        // You can either:
        // Option 1: Call delete endpoint for each product individually
        const deletePromises = productIds.map(id =>
          axiosConfig.get(`delete-feature-type/${id}`, setHeader())
        );

        Promise.all(deletePromises)
          .then((responses) => {
            const hasError = responses.some(res => res.data.error);

            if (!hasError) {
              SweetAlert.fire({
                title: "Success",
                text: `All products in "${sectionName}" section have been deleted successfully.`,
                icon: "success",
              });
              getTypeData();
            } else {
              SweetAlert.fire({
                title: "Partial Success",
                text: "Some products could not be deleted. Please try again.",
                icon: "warning",
              });
              getTypeData();
            }
          })
          .catch((error) => {
            console.error("Error deleting section data:", error);
            SweetAlert.fire({
              title: "Error",
              text: "An error occurred while deleting the section data.",
              icon: "error",
            });
          });
      }
    });
  };

  const getTypeData = async () => {
    setLoading(true); // Start loading
    
    try {
      // Use Promise.all to run both API calls simultaneously
      const [sectionsResponse, typesResponse] = await Promise.all([
        axiosConfig.get("all-sections", setHeader()),
        axiosConfig.get("feature-types-list", setHeader())
      ]);

      // Handle sections data
      const sectionsData = sectionsResponse.data.data;
      const uniqueSections = [...new Set(sectionsData.map(item => item.section_name))];
      setSections(uniqueSections);

      // Set first section as active tab
      if (uniqueSections.length > 0 && !activeTab) {
        setActiveTab(uniqueSections[0]);
      }


      // Handle feature types data
      const typeData = typesResponse.data.data;
      setTypeData(typeData);

    } catch (error) {
      console.error("Error fetching data:", error);
      // Optionally show error message
      SweetAlert.fire({
        title: "Error",
        text: "Failed to fetch data. Please try again.",
        icon: "error",
      });
    } finally {
      setLoading(false); // Stop loading regardless of success or failure
    }
  };

  // Filter products based on active tab
  const getFilteredProducts = () => {
    return typeData.filter(item => item.section_name === activeTab);
  };

  // Parse images array
  const parseImages = (imagesString) => {
    try {
      const images = JSON.parse(imagesString);
      return Array.isArray(images) ? images : [];
    } catch (e) {
      return [];
    }
  };

 

  // Show loading component
  if (loading) {
    return (
      <LoadingComponent/>
    );
  }

  // Show no data only when not loading and no sections found
  if (!loading && sections.length === 0) {
    return <NoDataHandler title='No Feature Types Found' />;
  }

  return (
    <Fragment>
      <Breadcrumb />

      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            {/* Tab Navigation */}
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" role="tablist">
                  {sections.map((section, index) => (
                    <li className="nav-item" key={index}>
                      <button
                        className={`nav-link ${activeTab === section ? 'active' : ''}`}
                        onClick={() => setActiveTab(section)}
                        type="button"
                        style={{
                          border: 'none',
                          background: activeTab === section ? '#007bff' : 'transparent',
                          color: activeTab === section ? '#fff' : '#007bff',
                          borderRadius: '4px 4px 0 0',
                          marginRight: '2px'
                        }}
                      >
                        {section}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tab Content */}
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane fade show active">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h5 style={{ margin: 0 }}>{activeTab} Products</h5>
                      <span className="badge badge-primary">
                        {getFilteredProducts().length} products
                      </span>
                    </div>

                    {getFilteredProducts().length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead style={{ backgroundColor: '#f8f9fa' }}>
                            <tr>
                              <th scope="col">#</th>
                              <th scope="col">Product Name</th>
                              <th scope="col">Product ID</th>
                              <th scope="col">From Date</th>
                              <th scope="col">To Date</th>
                              <th scope="col">Date Check</th>
                              <th scope="col">Images</th>
                              <th scope="col">Status</th>
                              <th scope="col">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getFilteredProducts().map((item, index) => {
                              const images = parseImages(item.images);
                              const firstImage = images.length > 0 ? images[0] : null;

                              return (
                                <tr key={item.f_id}>
                                  <th scope="row">{index + 1}</th>
                                  <td>
                                    <strong>{item.productName}</strong>
                                  </td>
                                  <td>
                                    <span className="badge badge-info">{item.product_id}</span>
                                  </td>
                                  <td>{formatDate(item.from_date)}</td>
                                  <td>{formatDate(item.to_date)}</td>
                                  <td>
                                    <span
                                      className={`badge ${item.IsDatecheck === '1' ? 'badge-success' : 'badge-secondary'}`}
                                    >
                                      {item.IsDatecheck === '1' ? 'Enabled' : 'Disabled'}
                                    </span>
                                  </td>
                                  <td>
                                    {firstImage ? (
                                      <img
                                        src={`${SITE_URL}/images/product/${firstImage}`}
                                        alt={`Product ${item.productName}`}
                                        width="40"
                                        height="40"
                                        style={{
                                          objectFit: 'cover',
                                          borderRadius: '4px',
                                          border: '1px solid #dee2e6'
                                        }}
                                      />
                                    ) : (
                                      <span style={{ color: '#6c757d', fontSize: '12px' }}>
                                        No images
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <span
                                      className={`badge ${item.section_status === '1' ? 'badge-success' : 'badge-danger'}`}
                                    >
                                      {item.section_status === '1' ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="btn-group btn-group-sm" role="group">
                                     
                                      <button
                                        className="btn btn-outline-success btn-sm"
                                        title="Edit"
                                        onClick={() => navigate(`/pages/feature-type/edit/${item.f_id}`)}
                                        style={{ marginRight: '2px' }}
                                      >
                                        ✏️
                                      </button>
                                      <button
                                        className="btn btn-outline-danger btn-sm"
                                        title="Delete"
                                        onClick={() => DeeleteOneFeaturData(item.f_id)}
                                      >
                                        🗑️
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {/* Fixed Delete All Button */}
                        <div className="mt-4">
                          <button
                            className='btn btn-danger'
                            onClick={() => DeleteAllSectionData(activeTab)}
                            disabled={getFilteredProducts().length === 0}
                          >
                            Delete All {activeTab} Products ({getFilteredProducts().length})
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <p style={{ color: '#6c757d', margin: 0 }}>
                          No products found in this section.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AllFeatureTypes;